import * as React from 'react';
import { connect } from 'react-redux';
import { components } from 'react-select';
import { OptionProps } from 'react-select/src/components/Option';
import { ValueType } from 'react-select/src/types';
import { Button, Col, Row } from 'reactstrap';
import makeComponentTrashable, { TrashableProps } from 'trashable-react';
import { apiRequest } from '../../APIClient';
import { DEFAULT_ROUTE_PATH, SELECT_STYLE } from '../../constants';
import { sessionSelector } from '../../reducers/session';
import { Dispatch, OptionType, SwitchableCustomerType } from '../../types';
import InputDropdown from '../../widgets/InputDropdown';
import LoadingIndicator from '../../widgets/LoadingIndicator';
import Panel from '../../widgets/Panel';
import ViewHeader from '../../widgets/ViewHeader';
import Viewport from '../../widgets/Viewport';
import { PageTitle } from '../../widgets/PageTitle';

type OwnProps = {};

type IdToRiskIdMap = { [key: string]: string | undefined };

export type StoreProps = {
  currentCustomerId: string | null;
  customers: Array<SwitchableCustomerType>;
};

export type DispatchProps = {
  getCustomerNames: () => void;
  switchCustomer: (id: string, riskID: string | undefined) => Promise<any>;
};

export type Props = OwnProps & StoreProps & DispatchProps & TrashableProps;

type State = {
  nextCustomerId: string;
  nextRiskID: string | undefined;
  custToRiskIdMap: IdToRiskIdMap;
  submitting: boolean;
};

class CustomerSwitchView extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      nextCustomerId: '',
      nextRiskID: '',
      custToRiskIdMap: {},
      submitting: false,
    };
  }

  /**
   * Custom 'Option' (the value displayed in dropdown in react-select) that
   * shows the customer ID in brackets at the far right of the option
   */
  Option = (optionProps: OptionProps<OptionType>) => {
    const { data, children, ...rest } = optionProps;
    return (
      <components.Option {...rest} data={data} className="d-flex">
        <div className="flex-grow">{data.label}</div>
        <div className="align-self-end">({data.value})</div>
      </components.Option>
    );
  };

  componentDidMount() {
    this.props.getCustomerNames();
  }

  componentDidUpdate(prevProps: Props) {
    const prevCustomers = prevProps.customers;
    const { customers } = this.props;
    if (customers !== prevCustomers) {
      const tempObj: IdToRiskIdMap = {};
      customers.forEach((cust) => (tempObj[cust.id] = cust.riskID));
      this.setState({ custToRiskIdMap: tempObj });
    }
  }

  updateValue = (customer: ValueType<OptionType>) => {
    const { custToRiskIdMap } = this.state;
    const nextCustomerId = customer ? (customer as OptionType).value : '';
    const nextRiskID = customer
      ? custToRiskIdMap[(customer as OptionType).value]
      : '';
    this.setState({ nextCustomerId, nextRiskID });
  };

  onSubmit = async () => {
    const { registerPromise, switchCustomer } = this.props;
    this.setState({ submitting: true });
    try {
      await registerPromise(
        switchCustomer(this.state.nextCustomerId, this.state.nextRiskID),
      );
      this.setState({ submitting: false });
    } catch (err) {
      this.setState({ submitting: false });
    }
  };

  render() {
    const { customers, currentCustomerId } = this.props;
    const { nextCustomerId, submitting } = this.state;
    return (
      <Viewport>
        <PageTitle title="Switch Customers" />
        <ViewHeader title="Switch Customers" />
        <Panel data-cy="customer-switch" title="Customers">
          <InputDropdown
            options={customers
              .map((customer) => ({
                value: customer.id,
                label: customer.name,
              }))
              .filter((option) => option.value !== currentCustomerId)}
            components={{ Option: this.Option }}
            showDropdownArrow={true}
            autoFocus
            styles={SELECT_STYLE}
            onChange={this.updateValue}
            data-cy={'customer-name'}
            className="mt-2"
          />
          <Row>
            <Col xs={12} className="text-right mt-3">
              <Button
                className="ml-3"
                color="primary"
                size="lg"
                onClick={this.onSubmit}
                disabled={submitting || !nextCustomerId}
                data-cy={'switch-customer'}
              >
                {submitting && <LoadingIndicator className="mr-3" />}
                Switch Customer
              </Button>
            </Col>
          </Row>
        </Panel>
      </Viewport>
    );
  }
}

export default connect<StoreProps, DispatchProps, OwnProps>(
  (store): StoreProps => ({
    currentCustomerId: sessionSelector.getCustomerID(store),
    customers: sessionSelector.getSwitchableCustomers(store),
  }),
  (dispatch: Dispatch): DispatchProps => ({
    getCustomerNames: () => dispatch(apiRequest.getCustomerNames()),
    switchCustomer: (id: string, riskID: string | undefined = undefined) =>
      dispatch(apiRequest.switchCustomer(id, riskID, DEFAULT_ROUTE_PATH)),
  }),
)(makeComponentTrashable(CustomerSwitchView));
